import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react'
import { SketchPicker } from 'react-color'
import { InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import debounce from 'lodash-es/debounce'
import { useField } from 'formik'
import PropTypes from 'prop-types'
import { tooltipOverlay } from 'cg/components/shared/tooltipOverlay'

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const ColorPicker = ({ value, name, inModal }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false)
  const [pickerPosition, setPickerPosition] = useState(null)

  const inputTextRef = useRef()
  const colorPickerPopoverRef = useRef()
  const [field, meta, helpers] = useField(name)
  const { setValue } = helpers

  const prevDisplayColorPicker = usePrevious(displayColorPicker)

  const calculatePickerPosition = useCallback(() => {
    const popoverBoundingClientRect = colorPickerPopoverRef.current.getBoundingClientRect()
    const inputTextBoundingClientRect = inputTextRef.current.getBoundingClientRect()
    const top = inputTextBoundingClientRect.y + inputTextBoundingClientRect.height
    const left = inputTextBoundingClientRect.x - popoverBoundingClientRect.width + inputTextBoundingClientRect.width

    const style = {
      top: `${top}px`,
      left: `${left}px`
    }

    if (
      inputTextBoundingClientRect.bottom + popoverBoundingClientRect.height >=
      (window.innerHeight || document.documentElement.clientHeight)
    ) {
      style.top = `${top - popoverBoundingClientRect.height - inputTextBoundingClientRect.height}px`
    }
    return style
  }, [])

  const resizeListener = useCallback(() => {
    if (colorPickerPopoverRef && colorPickerPopoverRef.current) {
      setPickerPosition(calculatePickerPosition())
    }
  }, [calculatePickerPosition])

  useEffect(() => {
    window.addEventListener('resize', debounce(resizeListener, 300))
  })

  useEffect(
    () => () => {
      window.removeEventListener('resize', debounce(resizeListener, 300))
    },
    [resizeListener]
  )

  useEffect(() => {
    if (!prevDisplayColorPicker && displayColorPicker) {
      setPickerPosition(calculatePickerPosition())
    }
  }, [prevDisplayColorPicker, displayColorPicker, calculatePickerPosition])
  return (
    <>
      <OverlayTrigger placement="bottom" overlay={overlayProps => tooltipOverlay(<Tooltip>Label Color</Tooltip>, overlayProps)}>
        <StyledInputGroupText
          onClick={() => {
            setDisplayColorPicker(true)
          }}
          variant="secondary"
          title="Edit label color"
          ref={inputTextRef}
        >
          <ColorPickerIcon>
            <i style={{ color: value }} className="fas fa-square square-colorpicker" />
          </ColorPickerIcon>
        </StyledInputGroupText>
      </OverlayTrigger>
      {displayColorPicker &&
        inputTextRef &&
        ReactDOM.createPortal(
          <ColorPickerPopover style={pickerPosition} ref={colorPickerPopoverRef} $inModal={inModal}>
            <ColorPickerCover onClick={() => setDisplayColorPicker(false)} />
            <SketchPicker color={value} onChange={color => setValue(color.hex)} />
          </ColorPickerPopover>,
          document.body
        )}
    </>
  )
}

ColorPicker.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string,
  inModal: PropTypes.bool
}

const StyledInputGroupText = styled(InputGroup.Text)`
  &:hover,
  &:focus {
    background-color: ${props => props.theme.hoverBgLight};
  }
`

const ColorPickerPopover = styled.div`
  position: absolute;
  padding-top: 5px;
  padding-bottom: 5px;
  width: 220px;
  z-index: ${props => (props.$inModal ? 10003 : 3)};
  .sketch-picker {
    background: ${props => props.theme.modalBg} !important;
    box-shadow: none !important;
    border: 1px solid ${props => props.theme.borderColor};
    .flexbox-fix {
      border-top: 1px solid ${props => props.theme.borderColor} !important;
    }
  }
`

const ColorPickerCover = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`

const ColorPickerIcon = styled.span``

export default ColorPicker
